<template>
  <div v-if="currentUser">
    <div class="mb-2">
      <div class="d-flex mb-4 items-end">
        <h3 class="page-header-2 mb-1">Addresses</h3>
        <ActionsMenu
          class="ml-auto"
          :actions="actions"
          placement="bottom"
          @add="toggleAddressModal"
          @merge="toggleMergeModal"
        >
          <template #trigger>
            <sera-button
              icon="chevron-down"
              iconRight
              data-cy="address-actions-trigger"
              label="Address Actions"
              class="p-0"
            />
          </template>
        </ActionsMenu>
      </div>
      <div
        class="d-flex justify-content-between flex-wrap flex-col md:flex-row items-start md:items-center"
      >
        <div class="d-flex">
          <sera-input
            data-cy="address-search"
            :value="search"
            placeholder="Search"
            icon="magnifying-glass"
            class="mb-0 mr-2"
            @change="({ detail }) => (search = detail)"
          />
        </div>
        <ToggleSwitch
          v-model="active"
          label-on="Active"
          label-off="All"
          class="mt-2 md:mt-0"
        />
      </div>
    </div>
    <div
      class="d-flex align-items-stretch flex-wrap addresses-cont mt-4 mb-4 gap-4"
    >
      <AddressCard
        v-for="address in addresses"
        :key="address.id"
        :address="address"
        :memberships="membershipsForAddress(address)"
        class="w-full"
        @edit-tax-settings="toggleTaxSettingsModal($event)"
        @edit-address="toggleAddressModal($event)"
        @schedule="handleSchedule(address)"
        @archive="handleArchive($event)"
        @unarchive="handleUnarchive($event)"
      />
      <div v-if="!addresses?.length" class="no-results-message full-width my-4">
        No Results Available
      </div>
    </div>
    <PaginationControls
      v-if="addressPager"
      :pager="addressPager"
      :default-limit="10"
      @update:data="updateData"
    />
    <MergeAddressesModal v-if="mergeModal" @hide-modal="toggleMergeModal" />
    <AddressModal
      v-if="showAddressModal"
      :address="currentAddress || undefined"
      :customer="currentUser"
      @hide-modal="toggleAddressModal"
    />
    <div v-if="currentAddress">
      <TaxSettingsModal
        :gid="currentAddress.gid"
        :context="currentAddress"
        context-type="Address"
        :settings="currentAddress.tax_setting"
        :open="showTaxSettingsModal"
        @close="showTaxSettingsModal = false"
        @refresh="refreshDataForTaxModal"
      >
        <template #header>
          <div class="sera-body-3">
            <strong class="sera-bold">Address: </strong>
            <span>{{ fullAddress(currentAddress) }}</span>
          </div>
        </template>
      </TaxSettingsModal>
    </div>
    <ScheduleJobModal
      v-if="scheduleModal"
      :address="currentAddress || undefined"
      :customer="currentUser"
      :link="currentAddress?.links?.job_create"
      @hide-modal="handleModalClose"
    />
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import API from "serviceshift-ui/api-client";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import Pagination from "@/lib/pagination";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";
import { useAccessRole } from "@/lib/useAccessRole";

import PaginationControls from "@/components/PaginationControls.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import AddressModal from "@/Customer/AddressModal/AddressModal.vue";
import MergeAddressesModal from "@/Customer/MergeAddressesModal.vue";
import { fullAddress } from "@/mixins";
import ScheduleJobModal from "@/ScheduleModal/ScheduleJobModal.vue";
import TaxSettingsModal from "@/Taxability/TaxSettingsModal.vue";
import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";
import { CustomerMembership } from "@/typings/memberships";
import { hasMissingTaxZone } from "@/typings/taxability";
import AddressCard from "./AddressCard.vue";

const store = useStore();
const route = useRoute();
const mergeModal = ref(false);
const currentAddress = ref<Address | null>(null);
const showTaxSettingsModal = ref(false);
const showAddressModal = ref(false);
const scheduleModal = ref(false);
const search = ref("");
// true = Active (non-archived only); false = All (active + archived).
const active = ref(true);
const addresses = ref<Address[]>([]);
const addressPager = ref<Pagination | null>(null);
const memberships = ref<CustomerMembership[]>([]);
const actions = computed(() => {
  return [
    {
      label: "Add Address",
      eventName: "add",
      icon: "fa-regular fa-plus"
    },
    {
      label: "Merge Addresses",
      eventName: "merge",
      icon: "fa-regular fa-compress-alt",
      disabled: !mergeEnabled.value,
      visible: canAccess("merge_tools")
    }
  ];
});

onMounted(() => {
  if (currentUser.value) {
    init();
  }
});

const { canAccess } = useAccessRole();
const currentUser = computed(() => {
  return store.state.currentUser! as Customer;
});
const mergeEnabled = computed(function () {
  const tenant = store.state.tenant;
  return tenant ? tenant.merge_tools_enabled : false;
});
const init = async () => {
  addressPager.value = new Pagination({
    baseURL: "/admin/v4/addresses",
    perPage: 10,
    filteredBy: [{ key: "customer_id", value: currentUser.value.id }],
    sortedBy: [
      { key: "primary", direction: "desc" },
      { key: "created_at", direction: "asc" }
    ]
  });
  applyArchivedFilter();
  await fetchData();
  fetchMemberships();
  checkScheduleParams();
};

// Single source of truth for the Active/All view: Active restricts to
// non-archived; All removes the filter entirely (Pagination deletes a filter
// when its value is null), so the endpoint returns active + archived.
function applyArchivedFilter() {
  addressPager.value?.filter({
    key: "archived",
    value: active.value ? false : null
  });
}

async function fetchMemberships() {
  const url = currentUser.value?.links?.fetch_memberships_paginated;
  if (!url) return;
  try {
    const pager = new Pagination({ baseURL: url, perPage: 999 });
    const response = await pager.getData();
    if (response?.data?.data) {
      memberships.value = response.data.data;
    }
  } catch {
    // Non-critical display data — fail silently
  }
}

// m.active is a defensive client-side guard; the API already returns
// active: false for expired memberships but this prevents stale data
// from ever rendering a badge.
function membershipsForAddress(address: Address): CustomerMembership[] {
  return memberships.value.filter(
    (m) => m.active && m.address_ids?.includes(address.id)
  );
}

async function refreshDataForTaxModal() {
  await store.dispatch("fetchUser", { id: currentUser.value.id });
  await fetchData();
  setSelectedAddress(
    addresses.value.find((address) => address.id === currentAddress.value!.id)!
  );
}

const fetchData = async () => {
  const res = await addressPager.value?.getData();
  addresses.value = res?.data.data;
};

const checkScheduleParams = async () => {
  // Fetch address by id
  // pass it to handle schedule
  const { schedule_address_id } = getQueryVariables();
  if (!schedule_address_id) return;
  let address = addresses.value.find(
    (address) => address.id === schedule_address_id
  );
  if (!address) {
    const res = await API.makeRequest(`/v2/addresses/${schedule_address_id}`, {
      method: "GET"
    });
    address = res.data;
  }
  if (address) {
    handleSchedule(address);
  }
};
const handleSchedule = (address: Address) => {
  if (address.tax_setting) {
    if (hasMissingTaxZone(address.tax_setting)) {
      window.notify.error(
        "Please correct the customer's tax zone to schedule an appointment"
      );
      return;
    }
  }
  if (address.invalid && !address.manual_entry) {
    window.notify.error(
      "Please correct the customer's address to schedule an appointment"
    );
    return;
  } else {
    toggleScheduleModal(address, true);
  }
};
const handleModalClose = () => {
  // Remove schedule query param
  updateQueryParam("schedule_address_id", null, true);
  toggleScheduleModal(null, false);
};
const toggleTaxSettingsModal = (address: Address | null) => {
  showTaxSettingsModal.value = !showTaxSettingsModal.value;
  setSelectedAddress(address);
};
const toggleAddressModal = (address: Address | null) => {
  showAddressModal.value = !showAddressModal.value;
  setSelectedAddress(address);
};
const toggleMergeModal = () => {
  mergeModal.value = !mergeModal.value;
  if (!mergeModal.value) {
    fetchData();
  }
};
const toggleScheduleModal = (address: Address | null, modalOpen = false) => {
  scheduleModal.value = modalOpen;
  setSelectedAddress(address);
};
const setSelectedAddress = function (address: Address | null) {
  if (address && address.id) {
    currentAddress.value = new Address(address);
  } else {
    currentAddress.value = null;
  }
};
const handleSearch = debounce(async (val) => {
  if (addressPager.value) {
    const res = await addressPager.value.search(val);
    addresses.value = res?.data.data;
  }
}, 250);
const updateData = (data) => {
  addresses.value = data;
};

const handleArchive = (address: Address) =>
  performArchival(address.links?.archive, "archived");
const handleUnarchive = (address: Address) =>
  performArchival(address.links?.unarchive, "unarchived");

// Archived addresses are hidden from the Active view, so after archiving while
// viewing Active the card disappears on refetch — that is the expected outcome.
async function performArchival(url: string | undefined, label: string) {
  if (!url) return;
  try {
    // ignoreError suppresses the global interceptor's generic toast so we surface
    // only the specific message (e.g. the primary-address block) on failure.
    await API.makeRequest(url, {
      method: "PUT",
      headers: { ignoreError: true }
    });
    window.notify.success(`Address ${label} successfully`);
    await fetchData();
  } catch (e: any) {
    const message =
      e?.response?.data?.errors?.[0] || `Address could not be ${label}`;
    window.notify.error(message);
  }
}

watch(search, (val) => {
  handleSearch(val);
});
watch(active, () => {
  applyArchivedFilter();
  fetchData();
});
watch(currentUser, (val) => {
  if (val) {
    init();
  }
});
watch(
  () => route.query.schedule_address_id,
  (val, oldVal) => {
    if (val && val !== oldVal) {
      checkScheduleParams();
    }
  }
);
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.addresses-cont {
  gap: 20px 15px;
}
</style>
